import { unref } from 'vue'
import type { LastCartItem } from '../composables'
import { getMaxAssortNum, getPriceList, getSpecListByPriceList, useGlobal, useWindow } from '../composables'
import { useOrder } from '../composables/use-order'
import { createProductSpec } from '../composables/use-product'
import { ProductSpecModel } from '../model'
import { useApp } from '../store/modules/app'
import { useDropShip } from '../store/modules/dropShip'
import { useProductSpec } from '../store/modules/productSpec'
import type { LocationMethod, QuantitySize } from '../types'

const sizeMap = {
  "XS": 1,
  "S": 2,
  "M": 3,
  "L": 4,
  "XL": 5,
  "2XL": 6,
  "XXL": 6,
  "3XL": 7,
  "XXXL": 7,
  "4XL": 8,
  "XXXXL": 8,
  "5XL": 9,
  "XXXXXL": 9,
  "6XL": 10,
  "XXXXXXL": 10
}
// 提取所有 key 成联合类型
type SizeKey = keyof typeof sizeMap;
/*
 * 初始化尺寸和数量
 * @param isMultiSize 是否为多尺寸产品，决定是否需要初始化多个尺寸的数量
 */
export const initSizeQty = (isBlank: boolean, isMultiSize: boolean): void => {
  // 获取价格列表，根据是否为空状态来获取对应的产品规格
  if (isMultiSize) {
    const priceList = getPriceList({
      isBlank: isBlank,
      productSpecList: [],
      imprintMethodList: []
    })

  // 初始化尺寸数组，用于存储每个尺寸及其对应的价格
    const sizeObj: string[] = []

  // 遍历价格列表，将每个尺寸及其对应的价格存入尺寸数组
    priceList.forEach((f) => {
      for (const sz of f.Size) {
        if (!sizeObj.includes(sz)) {
          sizeObj.push(sz)
        }
      }
    });
    
    // 要把sizeArr排序处理
    const sizeArrSorted = sizeObj.sort((x, y) => sizeMap[x as SizeKey] - sizeMap[y as SizeKey]);

    const createQty = sizeArrSorted.map((key) => {
      return {
        size: key,
        quantity: '',
        price: 0,
        stock: undefined,
        display: true
      } as QuantitySize
    })

    // 将创建的数量对象赋给产品规格模型的createQty方法
    ProductSpecModel.prototype.createQty = () => createQty
  }
}

export const init = (
  isBlank: boolean
): {
  maxAssortNum: number
} => {
  const appState = useApp();
  const { productSpecList, locationGroupList, supportDropShip } = useWindow()
  const { queryQuantity, defaultMethod } = useGlobal()
  const { priceSysNo, imprintSysNo } = useOrder()

  // 先根据当前参数查询所有可用price list
  let allPriceList = getPriceList({
    isBlank: isBlank,
    productSpecList: [],
    imprintMethodList: []
  });
  // 如果没有找到可用price list,那就修改blank参数,重新查询一次
  if (allPriceList.length == 0) {
    isBlank = !isBlank;
    allPriceList = getPriceList({
      isBlank: isBlank,
      productSpecList: [],
      imprintMethodList: []
    });

    if (allPriceList.length > 0) {
      appState.setBlankValue(isBlank)
    }
  } else {
    /*
    如果地址栏里传递price sysno，那优先用地址栏传递过来的price sysno
    如果地址栏传递的price sysno没有找到，那继续用默认的price list
     */
    if (priceSysNo) {
      const tmpPriceList = allPriceList.filter((priceInfo) => {
        if (priceInfo.SysNo == priceSysNo) {
          return true
        }
        return false
      })
      if (tmpPriceList.length > 0) {
        allPriceList = tmpPriceList
      }
    }
    /* 如果有imprint sysno 那优先查询imprint sysno */
    if (imprintSysNo) {
      let imprintItem: LocationMethod | undefined;
      const locationGroup = locationGroupList.some(group => {
        return group.Methods.some(method => {
          if (method.SysNo == imprintSysNo) {
            imprintItem = method;
            return true;
          }
          return false;
        })
      });

      /*如果找到imprint method */
      if (imprintItem) {
        const tmpPriceList = allPriceList.filter((priceInfo) => {
          return priceInfo.ImprintMethodLimitation.length == 0 || priceInfo.ImprintMethodLimitation.includes(imprintItem!.Method);
        });
        if (tmpPriceList.length > 0) {
          allPriceList = tmpPriceList;
        }
      }
    }
  }

  const productSpecListState = useProductSpec()
  const dropshipState = useDropShip()

  // 查询price sysno的限制
  const specLimitation = getSpecListByPriceList(allPriceList, productSpecList, [], [])

  for (const key in specLimitation) {
    const element = specLimitation[key]

    const ps = productSpecList.find((f) => f.name === key)
    if (ps) {
      ps.values.forEach((val) => {
        if (element.includes(val.value)) {
          val.enabled = true
        } else {
          val.enabled = false
        }
      })
    }
  }
  const filterPriceList = allPriceList.filter((priceInfo) => {
    if (defaultMethod) {
      if (priceInfo.ImprintMethodLimitation.length > 0) {
        return priceInfo.ImprintMethodLimitation.includes(defaultMethod)
      }
    }

    return true;
  })


  const productSpecModel = createProductSpec(filterPriceList.length > 0 ? filterPriceList : allPriceList, queryQuantity)
  productSpecListState.add(productSpecModel)

  // 计算assorted num
  const currentPriceList = getPriceList({
    isBlank: isBlank,
    productSpecList: [productSpecModel],
    imprintMethodList: []
  })

  const maxAssortNum = getMaxAssortNum(getSpecListByPriceList(currentPriceList, productSpecList, [], []))

  if (supportDropShip) {
    dropshipState.setDropship({
      needDropShip: undefined,
      needAddMore: undefined,
      locationNum: '',
      addlProductionTime: 0
    })
  }
  return {
    maxAssortNum
  }
}
